import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

interface SentimentSummaryProps {
  data: {
    positive: number
    neutral: number
    negative: number
    unrated: number
  }
  avgQualificationScore?: number
  loading?: boolean
}

const sentiments = [
  { key: 'positive', label: 'Positive', color: 'bg-green-500' },
  { key: 'neutral', label: 'Neutral', color: 'bg-blue-400' },
  { key: 'negative', label: 'Negative', color: 'bg-red-500' },
  { key: 'unrated', label: 'Unrated', color: 'bg-gray-300' },
] as const

export default function SentimentSummary({ data, avgQualificationScore, loading = false }: SentimentSummaryProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Call Sentiment</CardTitle>
          <CardDescription>AI-detected caller sentiment</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Loading...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  const total = Object.values(data).reduce((sum, v) => sum + v, 0)

  const rated = data.positive + data.neutral + data.negative

  if (total === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Call Sentiment</CardTitle>
          <CardDescription>AI-detected caller sentiment</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center text-center py-8">
            <p className="text-muted-foreground font-medium">No sentiment data yet</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-xs">
              Sentiment is analyzed from call transcripts after each completed call. Make a few calls and data will appear here.
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (rated === 0 && data.unrated > 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Call Sentiment</CardTitle>
          <CardDescription>AI-detected caller sentiment</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center text-center py-8">
            <p className="text-muted-foreground font-medium">{data.unrated} calls — not yet analyzed</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-xs">
              Sentiment analysis runs automatically after calls complete. Recent calls may take a minute to process. Very short calls or failed connections may not generate sentiment data.
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Call Sentiment</CardTitle>
        <CardDescription>AI-detected caller sentiment</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {sentiments.map(({ key, label, color }) => {
            const value = data[key]
            const pct = total > 0 ? (value / total) * 100 : 0
            return (
              <div key={key}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium">{label}</span>
                  <span className="text-muted-foreground">{value} ({pct.toFixed(1)}%)</span>
                </div>
                <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${color}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )
          })}

          {avgQualificationScore != null && avgQualificationScore > 0 && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Avg Qualification Score</span>
                <span className="text-lg font-bold">{avgQualificationScore.toFixed(1)}<span className="text-sm text-muted-foreground">/10</span></span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
